/**
 * Lightweight toast notifications.
 *
 * Usage:
 *   showToast("Сообщение");
 *   showToast("Ошибка", "error");
 *   showToast("Успешно", "success");
 */

let container = null;

function getContainer() {
    if (!container) {
        container = document.createElement("div");
        container.className = "dl-toast-container";
        container.setAttribute("aria-live", "polite");
        container.setAttribute("aria-atomic", "false");
        document.body.appendChild(container);
    }
    return container;
}

/**
 * Confirm dialog via toast-style overlay.
 *
 * Usage:
 *   const ok = await showConfirm("Вы уверены?", "Доп. пояснение");
 */
export function showConfirm(title, description = "", confirmText = "Подтвердить", cancelText = "Отмена") {
    return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.className = "dl-confirm-overlay";

        const dialog = document.createElement("div");
        dialog.className = "dl-confirm";
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");

        const titleEl = document.createElement("p");
        titleEl.className = "dl-confirm__title";
        titleEl.textContent = title;

        dialog.appendChild(titleEl);

        if (description) {
            const descEl = document.createElement("p");
            descEl.className = "dl-confirm__desc";
            descEl.textContent = description;
            dialog.appendChild(descEl);
        }

        const actions = document.createElement("div");
        actions.className = "dl-confirm__actions";

        const cancelBtn = document.createElement("button");
        cancelBtn.type = "button";
        cancelBtn.className = "dl-confirm__btn dl-confirm__btn--cancel";
        cancelBtn.textContent = cancelText;

        const confirmBtn = document.createElement("button");
        confirmBtn.type = "button";
        confirmBtn.className = "dl-confirm__btn dl-confirm__btn--confirm";
        confirmBtn.textContent = confirmText;

        actions.appendChild(cancelBtn);
        actions.appendChild(confirmBtn);
        dialog.appendChild(actions);
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);

        requestAnimationFrame(() => overlay.classList.add("is-visible"));

        function close(result) {
            overlay.classList.remove("is-visible");
            overlay.addEventListener("transitionend", () => overlay.remove(), {once: true});
            resolve(result);
        }

        confirmBtn.addEventListener("click", () => close(true));
        cancelBtn.addEventListener("click", () => close(false));
        overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); });
    });
}

export function showToast(message, type = "error", duration = 5000) {
    const wrap = getContainer();
    const effectiveDuration = type === "error" ? duration * 2 : duration;

    const toast = document.createElement("div");
    toast.className = `dl-toast dl-toast--${type}`;
    toast.setAttribute("role", "status");

    const icon = document.createElement("span");
    icon.className = "dl-toast__icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = type === "success"
        ? `<svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`
        : type === "warning"
        ? `<svg viewBox="0 0 24 24"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>`
        : `<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>`;

    const text = document.createElement("span");
    text.className = "dl-toast__text";
    text.textContent = message;

    const close = document.createElement("button");
    close.className = "dl-toast__close";
    close.type = "button";
    close.setAttribute("aria-label", "Закрыть");
    close.innerHTML = `<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;

    toast.appendChild(icon);
    toast.appendChild(text);
    toast.appendChild(close);
    wrap.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => toast.classList.add("is-visible"));

    function dismiss() {
        toast.classList.remove("is-visible");
        toast.classList.add("is-hiding");
        toast.addEventListener("transitionend", () => toast.remove(), {once: true});
    }

    let timer = effectiveDuration > 0 ? setTimeout(dismiss, effectiveDuration) : null;

    // Pause auto-dismiss on hover, resume when mouse leaves
    toast.addEventListener("mouseenter", () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    });
    toast.addEventListener("mouseleave", () => {
        if (effectiveDuration > 0) {
            timer = setTimeout(dismiss, 1500);
        }
    });

    close.addEventListener("click", dismiss);
    close.addEventListener("click", () => { if (timer) clearTimeout(timer); }, {once: true});
}
